import React, { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle } from 'react';
import { initializeApp, getApps, getApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from "firebase/app-check";
import {
    getAuth,
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    sendEmailVerification,
} from 'firebase/auth';
import {
    checkUrlSafetyClient
} from './safeBrowsingClient';

import {
    checkAdultUrl
} from "./lib/adultFilterFromLists";

import {
    generateAIContent
} from "./api/aiService";


import {
    getFirestore,
    collection,
    query,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    orderBy,
    Timestamp,
    getDoc,
    setDoc,
    onSnapshot,
    initializeFirestore,
    CACHE_SIZE_UNLIMITED
} from 'firebase/firestore';
import {
    Plus,
    FilePenLine,
    QrCode,
    Trash2,
    Copy,
    LogOut,
    X,
    Loader2,
    Zap,
    Sparkles,
} from 'lucide-react';

// --- VENDORED DEPENDENCY (nanoid) ---
const nanoid = (size = 21) => {
    let id = '';
    let bytes = crypto.getRandomValues(new Uint8Array(size));
    const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ-_';
    while (size--) {
        id += ALPHABET[bytes[size] & 63];
    }
    return id;
};

// --- CONFIGURATION ---
const APP_NAME = process.env.REACT_APP_NAME || 'RuneQR'; // ✅ Fallback for local dev
const APP_DOMAIN = process.env.REACT_APP_DOMAIN || 'qr.welcomesphere.io';
const TRACK_SCAN_HTTP_URL = process.env.REACT_APP_TRACK_SCAN_HTTP_URL;

// ✅ Safety checks
if (process.env.NODE_ENV === 'development') {
    console.log('🏷️ App Name:', APP_NAME);
    console.log('🌐 Domain:', APP_DOMAIN);
    if (!TRACK_SCAN_HTTP_URL) {
        console.error('❌ REACT_APP_TRACK_SCAN_HTTP_URL not configured!');
    }
}


// ✅ Feature flags for future features
const FEATURES = {
    SHOW_TYPE_SELECTOR: false,  // Set to true when ready for campaigns/categories
    SHOW_TYPE_FILTER: false     // Set to true to enable type filtering in dashboard
};


// --- FIREBASE INITIALIZATION ---
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// STEP 1: Initialize App Check FIRST before other services
let appCheckInstance = null;

const siteKey = process.env.REACT_APP_RECAPTCHA_V3_SITEKEY; // use ONE consistent env var name

if (typeof window !== "undefined" && siteKey) {
    try {
        appCheckInstance = initializeAppCheck(app, {
            provider: new ReCaptchaV3Provider(siteKey),
            isTokenAutoRefreshEnabled: true,
        });

        console.log("Firebase App Check initialized successfully");

        // Optional: fetch a token once to confirm it's working (safe preview only)
        getToken(appCheckInstance, /* forceRefresh */ false)
            .then((token) => {
                console.log("App Check Token Retrieved", {
                    exists: !!token?.token,
                });
            })
            .catch((error) => {
                console.error("Failed to get App Check token", {
                    message: error?.message,
                    code: error?.code,
                });
            });
    } catch (error) {
        console.error("App Check initialization failed", {
            message: error?.message,
            code: error?.code,
            domain: window.location.hostname,
            siteKeyPreview: siteKey?.slice?.(0, 10),
        });
    }
} else {
    if (process.env.NODE_ENV === "production") {
        throw new Error("App Check site key missing (REACT_APP_RECAPTCHA_V3_SITEKEY).");
    }
}


// STEP 2: Initialize Auth AFTER App Check
const auth = getAuth(app);

// STEP 3: Initialize Firestore AFTER App Check
let db;
try {
    db = initializeFirestore(app, {
        cacheSizeBytes: CACHE_SIZE_UNLIMITED,  // Unlimited offline cache
    });
} catch (error) {
    // Already initialized (hot reload in dev)
    db = getFirestore(app);
}


const googleProvider = new GoogleAuthProvider();


// --- HELPERS ---
const formatDateTime = (date) => {
    if (!date) return 'Never';
    const d = date instanceof Date ? date : date.toDate?.() || new Date(date);
    return d.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const withTimeout = (promise, timeoutMs = 10000, operationName = 'Operation') => {
    return Promise.race([
        promise,
        new Promise((_, reject) =>
            setTimeout(
                () => reject(new Error(`${operationName} timed out after ${timeoutMs}ms. Please check your internet connection.`)),
                timeoutMs
            )
        )
    ]);
};


// ============================================================
// ANIMATED QR LOADER - PREMIUM SCANNING LINE (BRAND-MATCHED)
// ============================================================
const AnimatedQRLoader = ({
    variant = 'app',
    fullScreen = true,
    message = null,
    size = 'default'
}) => {
    const defaultMessages = {
        app: `Loading ${APP_NAME}...`,
        redirect: 'Redirecting...'
    };

    const displayMessage = message || defaultMessages[variant];

    const sizeClasses = {
        small: 'w-20 h-20',
        default: 'w-28 h-28',
        large: 'w-36 h-36'
    };

    const containerClasses = fullScreen
        ? 'fixed inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 flex flex-col items-center justify-center z-50'
        : 'flex flex-col items-center justify-center p-8';

    return (
        <div className={containerClasses}>
            <style>{`
                /* QR Code fade-in animation */
                @keyframes qrFadeIn {
                    from { 
                        opacity: 0; 
                        transform: scale(0.92); 
                    }
                    to { 
                        opacity: 1; 
                        transform: scale(1); 
                    }
                }
                .qr-container {
                    animation: qrFadeIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
                }

                /* Scanning line animation - smooth vertical sweep */
                @keyframes scanLine {
                    0% { 
                        top: -8%; 
                        opacity: 0;
                    }
                    10% {
                        opacity: 1;
                    }
                    90% {
                        opacity: 1;
                    }
                    100% { 
                        top: 108%; 
                        opacity: 0;
                    }
                }
                .scan-line {
                    animation: scanLine 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                }

                /* Subtle glow that follows the scan line */
                @keyframes scanGlow {
                    0% { 
                        transform: translateY(-20px);
                        opacity: 0;
                    }
                    10% {
                        opacity: 0.5;
                    }
                    90% {
                        opacity: 0.5;
                    }
                    100% { 
                        transform: translateY(calc(100% + 20px));
                        opacity: 0;
                    }
                }
                .scan-glow {
                    animation: scanGlow 2.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
                }

                /* Corner indicators pulse - refined timing */
                @keyframes cornerPulse {
                    0%, 100% { 
                        opacity: 0.5;
                        transform: scale(1);
                    }
                    50% { 
                        opacity: 1;
                        transform: scale(1.08);
                    }
                }
                .corner-indicator {
                    animation: cornerPulse 2.2s ease-in-out infinite;
                }

                /* Text fade in */
                @keyframes fadeInDelayed {
                    from { 
                        opacity: 0; 
                        transform: translateY(8px); 
                    }
                    to { 
                        opacity: 1; 
                        transform: translateY(0); 
                    }
                }
                .fade-in-delayed {
                    animation: fadeInDelayed 0.6s ease-out 0.4s forwards;
                    opacity: 0;
                }

                /* Subtle breathing effect on QR code */
                @keyframes qrBreath {
                    0%, 100% { 
                        transform: scale(1);
                    }
                    50% { 
                        transform: scale(1.02);
                    }
                }
                .qr-breath {
                    animation: qrBreath 3s ease-in-out infinite;
                }
            `}</style>

            {/* QR Code with Scanning Animation */}
            <div className={`relative ${sizeClasses[size]}`}>
                {/* Premium backdrop with subtle shadow */}
                <div className="absolute inset-0 bg-white rounded-2xl shadow-2xl"
                    style={{ boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.1)' }}>
                </div>

                {/* QR Code - Brand-matched design */}
                <div className="qr-container relative p-4">
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-full h-full qr-breath"
                        role="img"
                        aria-label="Loading animation"
                    >
                        {/* ✅ EXACT MATCH to your AppLogo component */}

                        {/* Top-left square */}
                        <rect x="4" y="4" width="7" height="7" rx="1.5" fill="#1f2937" />
                        <rect x="6" y="6" width="3" height="3" rx="0.5" fill="white" />

                        {/* Top-right square */}
                        <rect x="14" y="4" width="7" height="7" rx="1.5" fill="#1f2937" />
                        <rect x="16" y="6" width="3" height="3" rx="0.5" fill="white" />

                        {/* Bottom-left square */}
                        <rect x="4" y="14" width="7" height="7" rx="1.5" fill="#1f2937" />
                        <rect x="6" y="16" width="3" height="3" rx="0.5" fill="white" />

                        {/* Bottom-right data pattern - branded */}
                        <rect x="17" y="14" width="3" height="3" rx="0.5" fill="#1f2937" />
                        <rect x="14" y="14" width="2" height="2" rx="0.5" fill="#1f2937" />
                        <rect x="14" y="18" width="2" height="2" rx="0.5" fill="#1f2937" />
                        <rect x="18" y="18" width="2" height="2" rx="0.5" fill="#1f2937" />
                        <rect x="18" y="14" width="2" height="2" rx="0.5" fill="#1f2937" />
                    </svg>

                    {/* Premium scanning frame with rounded corners */}
                    <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none">
                        {/* Corner indicators - refined iOS style with thicker borders */}
                        <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" fill="none">
                            {/* Top-left corner */}
                            <path
                                className="corner-indicator"
                                d="M 12 8 L 8 8 L 8 12"
                                stroke="#2563eb"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                fill="none"
                            />
                            {/* Top-right corner */}
                            <path
                                className="corner-indicator"
                                d="M 88 8 L 92 8 L 92 12"
                                stroke="#2563eb"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                fill="none"
                            />
                            {/* Bottom-left corner */}
                            <path
                                className="corner-indicator"
                                d="M 12 92 L 8 92 L 8 88"
                                stroke="#2563eb"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                fill="none"
                            />
                            {/* Bottom-right corner */}
                            <path
                                className="corner-indicator"
                                d="M 88 92 L 92 92 L 92 88"
                                stroke="#2563eb"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                fill="none"
                            />
                        </svg>
                    </div>

                    {/* Scanning line container */}
                    <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
                        {/* Premium glow effect - larger, more diffused */}
                        <div
                            className="scan-glow absolute left-0 right-0 h-16"
                            style={{
                                background: 'radial-gradient(ellipse at center, rgba(37, 99, 235, 0.15) 0%, rgba(37, 99, 235, 0.08) 40%, rgba(37, 99, 235, 0) 70%)',
                                filter: 'blur(12px)'
                            }}
                        ></div>

                        {/* Main scanning line - premium gradient */}
                        <div
                            className="scan-line absolute left-2 right-2 h-1"
                            style={{
                                background: 'linear-gradient(to right, transparent 0%, rgba(59, 130, 246, 0.3) 20%, #3b82f6 50%, rgba(59, 130, 246, 0.3) 80%, transparent 100%)',
                                boxShadow: '0 0 16px rgba(59, 130, 246, 0.6), 0 0 32px rgba(59, 130, 246, 0.3)',
                                borderRadius: '2px'
                            }}
                        ></div>

                        {/* Secondary scanning line - adds depth */}
                        <div
                            className="scan-line absolute left-2 right-2 h-px"
                            style={{
                                background: 'linear-gradient(to right, transparent 0%, rgba(147, 197, 253, 0.6) 50%, transparent 100%)',
                                top: '5px',
                                opacity: '0.5'
                            }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Loading message with premium typography */}
            {displayMessage && (
                <p className="mt-8 text-gray-800 font-semibold text-lg tracking-tight fade-in-delayed"
                    style={{ letterSpacing: '-0.01em' }}>
                    {displayMessage}
                </p>
            )}

            {/* Subtle hint text */}
            <p className="mt-2 text-gray-400 text-xs fade-in-delayed" style={{ animationDelay: '0.6s', opacity: 0 }}>
                Powered by {APP_NAME}
            </p>

            <span className="sr-only" role="status" aria-live="polite">
                {displayMessage}
            </span>
        </div>
    );
};


// --- AUTH HOOK ---
const useFirebaseAuth = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const authService = useMemo(() => ({
        login: (email, password) => signInWithEmailAndPassword(auth, email, password),
        signup: async (email, password) => {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            await sendEmailVerification(userCredential.user);
            return userCredential;
        },
        logout: () => signOut(auth),
        googleLogin: () => signInWithPopup(auth, googleProvider),
        resendVerificationEmail: () => {
            if (auth.currentUser) {
                return sendEmailVerification(auth.currentUser);
            }
            throw new Error("No user is currently signed in to resend verification email.");
        }
    }), []);

    return { user, loading, ...authService };
};

// ✅ NEW: Online/Offline Detection Hook
const useOnlineStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    return isOnline;
};


// --- FIRESTORE HOOK ---
const useFirebaseFirestore = (user) => {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const APPIDPROXY = firebaseConfig.projectId;


    const linksCollectionRef = useMemo(() => {
        if (!user) return null;
        return collection(db, 'artifacts', APPIDPROXY, 'users', user.uid, 'links');
    }, [user, APPIDPROXY]);

    useEffect(() => {
        if (!user || !linksCollectionRef) {
            setLinks([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const q = query(linksCollectionRef, orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
                const fetchedLinks = querySnapshot.docs.map((docSnap) => {
                    const data = docSnap.data();
                    return {
                        id: docSnap.id,
                        ...data,
                        createdAt: data.createdAt?.toDate() || new Date(),
                        updatedAt: data.updatedAt?.toDate() || null,
                        lastScannedAt: data.lastScannedAt?.toDate() || null,
                        notes: data.notes || '',
                        // ✅ NEW: Default type to 'generic' for backward compatibility
                        type: data.type || 'generic'
                    };
                });
                setLinks(fetchedLinks);
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching real-time links:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [user, linksCollectionRef]);

    const createLink = useCallback(async (data) => {
        if (!user || !linksCollectionRef) {
            throw new Error('User or collection not configured.');
        }

        if (!auth.currentUser) {
            throw new Error('You must be online and authenticated to create links.');
        }

        try {
            await auth.currentUser.getIdToken(true);
        } catch (authError) {
            throw new Error('Authentication expired. Please sign in again.');
        }

        const shortId = data.isDynamic ? nanoid(7) : null;
        const now = Timestamp.now();

        const newLinkData = {
            name: data.name,
            destinationUrl: data.destinationUrl,
            isDynamic: data.isDynamic,
            notes: data.notes || '',
            // ✅ UPDATED: Default to 'generic' instead of 'qr'
            type: data.type || 'generic',
            ownerUid: auth.currentUser.uid,
            shortId,
            scanCount: 0,
            scans: { ios: 0, android: 0, other: 0 },
            createdAt: now,
            updatedAt: now,
            lastScannedAt: null
        };

        const docRef = await withTimeout(
            addDoc(linksCollectionRef, newLinkData),
            10000,
            'Create link'
        );

        if (data.isDynamic && shortId) {
            const publicLinkRef = doc(db, 'public_links', shortId);
            await withTimeout(
                setDoc(publicLinkRef, {
                    destinationUrl: data.destinationUrl,
                    ownerUid: auth.currentUser.uid,
                    privateLinkDocId: docRef.id
                }),
                10000,
                'Create public link'
            );
        }
    }, [user, linksCollectionRef]);

    const updateLink = useCallback(async (id, data) => {
        if (!user || !APPIDPROXY) {
            throw new Error('User or App ID missing.');
        }

        if (!auth.currentUser) {
            throw new Error('You must be online and authenticated to update links.');
        }

        try {
            await auth.currentUser.getIdToken(true);
        } catch (authError) {
            throw new Error('Authentication expired. Please sign in again.');
        }

        const linkRef = doc(db, 'artifacts', APPIDPROXY, 'users', auth.currentUser.uid, 'links', id);
        const currentLink = links.find(l => l.id === id);

        if (!currentLink) return;

        let updateData = {
            name: data.name,
            destinationUrl: data.destinationUrl,
            isDynamic: data.isDynamic,
            notes: data.notes || '',
            // ✅ UPDATED: Preserve type with 'generic' fallback
            type: data.type || currentLink.type || 'generic',
            updatedAt: Timestamp.now()
        };

        let shortId = currentLink.shortId;

        if (currentLink.isDynamic && !data.isDynamic) {
            if (shortId) {
                await withTimeout(
                    deleteDoc(doc(db, 'public_links', shortId)),
                    10000,
                    'Delete public link'
                );
            }
            updateData.shortId = null;
        } else if (!currentLink.isDynamic && data.isDynamic) {
            shortId = nanoid(7);
            updateData.shortId = shortId;
            await withTimeout(
                setDoc(doc(db, 'public_links', shortId), {
                    destinationUrl: data.destinationUrl,
                    ownerUid: auth.currentUser.uid,
                    privateLinkDocId: id
                }),
                10000,
                'Create public link'
            );
        } else if (data.isDynamic && shortId) {
            await withTimeout(
                updateDoc(doc(db, 'public_links', shortId), {
                    destinationUrl: data.destinationUrl
                }),
                10000,
                'Update public link'
            );
        }

        await withTimeout(
            updateDoc(linkRef, updateData),
            10000,
            'Update link'
        );
    }, [links, user, APPIDPROXY]);

    const deleteLink = useCallback(async (id) => {
        if (!user || !APPIDPROXY) {
            throw new Error('User or App ID missing.');
        }

        // ✅ Verify authentication
        if (!auth.currentUser) {
            throw new Error('You must be online and authenticated to delete links.');
        }

        // ✅ Force token refresh
        try {
            await auth.currentUser.getIdToken(true);
        } catch (authError) {
            throw new Error('Authentication expired. Please sign in again.');
        }

        const currentLink = links.find(l => l.id === id);
        if (currentLink && currentLink.isDynamic && currentLink.shortId) {
            const publicLinkRef = doc(db, 'public_links', currentLink.shortId);
            await withTimeout(
                deleteDoc(publicLinkRef),
                10000,
                'Delete public link'
            );
        }

        const linkRef = doc(db, 'artifacts', APPIDPROXY, 'users', auth.currentUser.uid, 'links', id);
        await withTimeout(
            deleteDoc(linkRef),
            10000,
            'Delete link'
        );
    }, [user, APPIDPROXY, links]);

    // ✅ CRITICAL: This return statement must be here
    return { links, loading, createLink, updateLink, deleteLink };
}; // ✅ CRITICAL: Close the useFirebaseFirestore function here


// --- QR CODE CANVAS ---
const QRCodeCanvas = React.forwardRef(({ value, size = 256 }, ref) => {
    const [qrCode] = useState(() => {
        if (typeof window !== 'undefined' && window.QRCodeStyling) {
            return new window.QRCodeStyling({
                width: size,
                height: size,
                data: value,
                margin: 0,
                dotsOptions: {
                    color: "#000000",
                    type: "rounded"
                },
                cornersSquareOptions: {
                    type: "extra-rounded"
                },
                backgroundOptions: {
                    color: "#ffffff"
                }
            });
        }
        return null;
    });

    const containerRef = useRef(null);

    useEffect(() => {
        if (qrCode && containerRef.current) {
            qrCode.append(containerRef.current);
        }
    }, [qrCode]);

    useEffect(() => {
        if (qrCode) {
            qrCode.update({
                data: value,
                width: size,
                height: size
            });
        }
    }, [qrCode, value, size]);

    useImperativeHandle(ref, () => ({
        download: (fileName) => {
            if (qrCode) {
                qrCode.download({ name: fileName, extension: "png" });
            }
        }
    }));

    return <div ref={containerRef} />;
});

// --- UI COMPONENTS ---

// 1. RuneQR Logo - Original Colors, Optimized for React
// --- UI COMPONENTS ---

// 1. RuneQR Logo (Fixed: Text is now vertically centered & Edges are smooth)
const RuneQRLogo = ({ className }) => (
    <svg 
        viewBox="0 105 384 140" // ✅ UPDATED: Adjusted y-offset (105) to push visual center down
        className={className}
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid meet"
        shapeRendering="geometricPrecision"
    >
        <defs>
            <clipPath id="a"><path d="M0 0h70v106H0z"/></clipPath>
            {/* ... keeping your existing clipPaths to save space ... */}
            <clipPath id="b"><path d="M301.012 177.941h38.55v37.102h-38.55Zm0 0"/></clipPath>
            <clipPath id="c"><path d="M305.512 177.941h29.457c2.484 0 4.5 2.016 4.5 4.5v28.102c0 2.484-2.016 4.5-4.5 4.5h-29.457a4.5 4.5 0 0 1-4.5-4.5v-28.102c0-2.484 2.015-4.5 4.5-4.5m0 0"/></clipPath>
            <clipPath id="e"><path d="M.012.941h38.55v37.102H.013Zm0 0"/></clipPath>
            <clipPath id="f"><path d="M4.512.941h29.457c2.484 0 4.5 2.016 4.5 4.5v28.102c0 2.484-2.016 4.5-4.5 4.5H4.512a4.5 4.5 0 0 1-4.5-4.5V5.441c0-2.484 2.015-4.5 4.5-4.5m0 0"/></clipPath>
            <clipPath id="d"><path d="M0 0h39v39H0z"/></clipPath>
            <clipPath id="g"><path d="M341.793 138.469h38.55v37.101h-38.55Zm0 0"/></clipPath>
            <clipPath id="h"><path d="M346.293 138.469h29.457c2.484 0 4.5 2.015 4.5 4.5v28.101c0 2.485-2.016 4.5-4.5 4.5h-29.457a4.5 4.5 0 0 1-4.5-4.5V142.97c0-2.485 2.016-4.5 4.5-4.5m0 0"/></clipPath>
            <clipPath id="j"><path d="M.793.469h38.55V37.57H.794Zm0 0"/></clipPath>
            <clipPath id="k"><path d="M5.293.469H34.75c2.484 0 4.5 2.015 4.5 4.5V33.07c0 2.485-2.016 4.5-4.5 4.5H5.293a4.5 4.5 0 0 1-4.5-4.5V4.97c0-2.485 2.016-4.5 4.5-4.5m0 0"/></clipPath>
            <clipPath id="i"><path d="M0 0h40v38H0z"/></clipPath>
            <clipPath id="l"><path d="M301.012 138.469h38.55v37.101h-38.55Zm0 0"/></clipPath>
            <clipPath id="m"><path d="M339.469 142.969v28.101c0 2.485-2.016 4.5-4.5 4.5h-29.457a4.5 4.5 0 0 1-4.5-4.5V142.97c0-2.485 2.015-4.5 4.5-4.5h29.457c2.484 0 4.5 2.015 4.5 4.5m0 0"/></clipPath>
            <clipPath id="o"><path d="M.012.469h38.55V37.57H.013Zm0 0"/></clipPath>
            <clipPath id="p"><path d="M38.469 4.969V33.07c0 2.485-2.016 4.5-4.5 4.5H4.512a4.5 4.5 0 0 1-4.5-4.5V4.97c0-2.485 2.015-4.5 4.5-4.5h29.457c2.484 0 4.5 2.015 4.5 4.5m0 0"/></clipPath>
            <clipPath id="n"><path d="M0 0h39v38H0z"/></clipPath>
            <clipPath id="q"><path d="M366.074 201.023h14.184v14.02h-14.184Zm0 0"/></clipPath>
            <clipPath id="r"><path d="M368.324 201.023H378a2.25 2.25 0 0 1 2.25 2.25v9.52a2.25 2.25 0 0 1-2.25 2.25h-9.676a2.25 2.25 0 0 1-2.25-2.25v-9.52a2.25 2.25 0 0 1 2.25-2.25m0 0"/></clipPath>
            <clipPath id="t"><path d="M.074.023h14.184v14.02H.074Zm0 0"/></clipPath>
            <clipPath id="u"><path d="M2.324.023H12a2.25 2.25 0 0 1 2.25 2.25v9.52a2.25 2.25 0 0 1-2.25 2.25H2.324a2.25 2.25 0 0 1-2.25-2.25v-9.52a2.25 2.25 0 0 1 2.25-2.25m0 0"/></clipPath>
            <clipPath id="s"><path d="M0 0h15v15H0z"/></clipPath>
            <clipPath id="v"><path d="M353.934 189.484h14.183V203.5h-14.183Zm0 0"/></clipPath>
            <clipPath id="w"><path d="M356.184 189.484h9.675a2.25 2.25 0 0 1 2.25 2.25v9.516a2.25 2.25 0 0 1-2.25 2.25h-9.675a2.25 2.25 0 0 1-2.25-2.25v-9.516a2.25 2.25 0 0 1 2.25-2.25m0 0"/></clipPath>
            <clipPath id="y"><path d="M.934.484h14.183V14.5H.934Zm0 0"/></clipPath>
            <clipPath id="z"><path d="M3.184.484h9.675a2.25 2.25 0 0 1 2.25 2.25v9.516a2.25 2.25 0 0 1-2.25 2.25H3.184a2.25 2.25 0 0 1-2.25-2.25V2.734a2.25 2.25 0 0 1 2.25-2.25m0 0"/></clipPath>
            <clipPath id="x"><path d="M0 0h16v15H0z"/></clipPath>
            <clipPath id="A"><path d="M341.793 177.941h14.184v14.02h-14.184Zm0 0"/></clipPath>
            <clipPath id="B"><path d="M344.043 177.941h9.676a2.25 2.25 0 0 1 2.25 2.25v9.52a2.25 2.25 0 0 1-2.25 2.25h-9.676a2.25 2.25 0 0 1-2.25-2.25v-9.52a2.25 2.25 0 0 1 2.25-2.25m0 0"/></clipPath>
            <clipPath id="D"><path d="M.793.941h14.184v14.02H.793Zm0 0"/></clipPath>
            <clipPath id="E"><path d="M3.043.941h9.676a2.25 2.25 0 0 1 2.25 2.25v9.52a2.25 2.25 0 0 1-2.25 2.25H3.043a2.25 2.25 0 0 1-2.25-2.25V3.19a2.25 2.25 0 0 1 2.25-2.25m0 0"/></clipPath>
            <clipPath id="C"><path d="M0 0h15v15H0z"/></clipPath>
        </defs>
        
        {/* Blue Icon Parts */}
        <g clipPath="url(#a)" transform="translate(233 134)">
            <path fill="#1f61de" d="M63.115 47.76q0 10.471-2.985 17.391c-1.992 4.606-4.765 8.149-8.328 10.625q-5.343 3.72-12.375 4.563v16.86q0 .842-.469 1.468-.468.623-1.312.625H31.99q-.832-.001-1.313-.625-.469-.626-.469-1.469v-16.86c-4.68-.632-8.796-2.171-12.359-4.608q-5.344-3.67-8.39-10.579-3.034-6.92-3.032-17.39V34.245q0-11.639 3.672-18.86 3.669-7.23 10.11-10.64Q26.643 1.34 34.817 1.34q8.28.001 14.61 3.406 6.34 3.41 10.015 10.64c2.445 4.813 3.672 11.102 3.672 18.86Zm-9.64 0V34.246c0-8.8-1.665-15.05-4.985-18.75q-4.97-5.561-13.672-5.562-8.579 0-13.672 5.562-5.08 5.55-5.078 18.75v13.516q-.001 13.19 5.078 18.75 5.093 5.548 13.672 5.547 8.59 0 13.625-5.547c3.351-3.707 5.031-9.957 5.031-18.75m0 0"/>
        </g>
        <g clipPath="url(#b)"><g clipPath="url(#c)"><g clipPath="url(#d)" transform="translate(301 177)"><g clipPath="url(#e)"><g clipPath="url(#f)">
            <path fill="#1f61de" d="M.012.941h38.406v37.102H.012Zm0 0"/>
        </g></g></g></g></g>
        <g clipPath="url(#g)"><g clipPath="url(#h)"><g clipPath="url(#i)" transform="translate(341 138)"><g clipPath="url(#j)"><g clipPath="url(#k)">
            <path fill="#1f61de" d="M.793.469h38.406V37.57H.793Zm0 0"/>
        </g></g></g></g></g>
        <g clipPath="url(#l)"><g clipPath="url(#m)"><g clipPath="url(#n)" transform="translate(301 138)"><g clipPath="url(#o)"><g clipPath="url(#p)">
            <path fill="#1f61de" d="M.012.469h38.406V37.57H.012Zm0 0"/>
        </g></g></g></g></g>
        <g clipPath="url(#q)"><g clipPath="url(#r)"><g clipPath="url(#s)" transform="translate(366 201)"><g clipPath="url(#t)"><g clipPath="url(#u)">
            <path fill="#1f61de" d="M.074.023h14.184v14.02H.074Zm0 0"/>
        </g></g></g></g></g>
        <g clipPath="url(#v)"><g clipPath="url(#w)"><g clipPath="url(#x)" transform="translate(353 189)"><g clipPath="url(#y)"><g clipPath="url(#z)">
            <path fill="#1f61de" d="M.934.484h14.183V14.5H.934Zm0 0"/>
        </g></g></g></g></g>
        <g clipPath="url(#A)"><g clipPath="url(#B)"><g clipPath="url(#C)" transform="translate(341 177)"><g clipPath="url(#D)"><g clipPath="url(#E)">
            <path fill="#1f61de" d="M.793.941h14.184v14.02H.793Zm0 0"/>
        </g></g></g></g></g>

        {/* Text Part (Black) */}
        <path fill="#000000" d="M56.135 212.854q-.001 1.36-1.375 1.36h-6.703c-.625 0-1.133-.051-1.515-.157q-.582-.153-1.204-1.203l-18.75-31.219H23.87q-2.627 0-4.985-.094a383 383 0 0 0-6.547-.218v30.797c0 .492-.18.964-.53 1.421q-.518.673-1.345.672H4.807q-.739.001-1.312-.625a2.08 2.08 0 0 1-.578-1.468V138.79q-.002-1.98 2.515-2.296 3.34-.422 8.625-.625 5.296-.219 9.813-.22 8.264.001 14.922 2.095 6.654 2.095 10.53 7.015 3.875 4.922 3.876 13.406v.735q-.001 8.799-4.672 14.047-4.658 5.237-11.781 7.11l19.062 31.858q.328.41.328.938M43.76 158.9v-.735c0-5.226-1.68-8.851-5.03-10.875q-5.017-3.046-14.86-3.046h-3.828q-2.251 0-4.344.046c-1.399.032-2.516.122-3.36.266v28.703q3.47.096 6.61.156 3.14.048 4.922.047c6.414 0 11.332-1.007 14.75-3.03q5.139-3.048 5.14-11.532M111.427 190.323q-.002 7.768-2.312 12.578-2.297 4.814-5.813 7.437c-2.336 1.75-4.703 2.961-7.109 3.625q-3.61.985-6.344.985h-.937c-1.75 0-3.852-.328-6.297-.985-2.438-.664-4.828-1.875-7.172-3.625-2.336-1.75-4.273-4.226-5.813-7.437q-2.296-4.81-2.296-12.578v-31.625q0-.843.515-1.47c.352-.413.813-.624 1.375-.624h5.438q.843.001 1.36.625c.35.418.53.906.53 1.469v31.625q-.001 6.298 1.625 9.906 1.624 3.61 4.453 5.14 2.825 1.517 6.282 1.516h.937q3.563 0 6.328-1.515 2.777-1.531 4.407-5.141 1.624-3.608 1.625-9.906v-31.625q0-.843.515-1.47c.352-.413.813-.624 1.375-.624h5.453q.827.001 1.344.625.53.626.531 1.469ZM169.769 212.12c0 .562-.18 1.054-.531 1.468q-.517.626-1.344.625h-5.453c-.563 0-1.024-.207-1.375-.625a2.23 2.23 0 0 1-.516-1.468v-37.391c0-3.914-.633-6.711-1.89-8.39q-1.875-2.515-7.22-2.516h-7.437q-5.345.001-7.234 2.515-1.875 2.52-1.875 8.39v37.392c0 .562-.18 1.054-.531 1.468q-.516.626-1.36.625h-5.437q-.847.001-1.375-.625a2.23 2.23 0 0 1-.516-1.468v-37.391q0-6.075 1.828-9.797 1.841-3.715 4.672-5.703 2.826-1.998 6.016-2.672 3.187-.686 5.812-.687h7.438q2.718 0 5.859.687 3.154.675 5.984 2.672 2.827 1.988 4.657 5.703 1.827 3.72 1.828 9.797ZM228.423 186.041q-.002 2.298-1.985 2.297h-35.093v1.47q0 9.328 3.656 13.202c2.445 2.586 6.082 3.875 10.906 3.875 3.07 0 6.352-.101 9.844-.312q5.233-.329 8.797-.844h.531c.414 0 .797.105 1.14.312.352.211.532.594.532 1.157v3.562q0 1.05-.422 1.625-.423.582-1.672.781-4.502.844-9.016 1.313-4.501.469-9.734.469-4.501-.002-8.75-1.203a20.6 20.6 0 0 1-7.64-4.032c-2.274-1.882-4.075-4.453-5.407-7.703q-1.985-4.874-1.984-12.203v-9.422q0-11.214 6.015-17.86 6.03-6.655 17.235-6.655 11.529 0 17.281 6.656 5.766 6.645 5.766 17.86Zm-9.313-4.718v-.938q-.001-8.905-3.406-12.672-3.41-3.78-10.328-3.78c-4.469 0-7.93 1.327-10.375 3.983q-3.656 3.985-3.656 12.47v.937Zm0 0"/>
        <path fill="#000000" d="M 51.21875 -2.09375 C 51.21875 -1.53125 51.039062 -1.039062 50.6875 -0.625 C 50.34375 -0.207031 49.894531 0 49.34375 0 L 43.890625 0 C 43.328125 0 42.867188 -0.207031 42.515625 -0.625 C 42.171875 -1.039062 42 -1.53125 42 -2.09375 L 42 -39.484375 C 42 -43.398438 41.367188 -46.195312 40.109375 -47.875 C 38.859375 -49.550781 36.453125 -50.390625 32.890625 -50.390625 L 25.453125 -50.390625 C 21.890625 -50.390625 19.476562 -49.550781 18.21875 -47.875 C 16.96875 -46.195312 16.34375 -43.398438 16.34375 -39.484375 L 16.34375 -2.09375 C 16.34375 -1.53125 16.164062 -1.039062 15.8125 -0.625 C 15.46875 -0.207031 15.015625 0 14.453125 0 L 9.015625 0 C 8.453125 0 7.992188 -0.207031 7.640625 -0.625 C 7.296875 -1.039062 7.125 -1.53125 7.125 -2.09375 L 7.125 -39.484375 C 7.125 -43.535156 7.734375 -46.800781 8.953125 -49.28125 C 10.179688 -51.757812 11.738281 -53.660156 13.625 -54.984375 C 15.507812 -56.316406 17.515625 -57.207031 19.640625 -57.65625 C 21.765625 -58.113281 23.703125 -58.34375 25.453125 -58.34375 L 32.890625 -58.34375 C 34.703125 -58.34375 36.65625 -58.113281 38.75 -57.65625 C 40.851562 -57.207031 42.847656 -56.316406 44.734375 -54.984375 C 46.617188 -53.660156 48.171875 -51.757812 49.390625 -49.28125 C 50.609375 -46.800781 51.21875 -43.535156 51.21875 -39.484375 Z M 51.21875 -2.09375" transform="translate(118.550006, 214.21332)"/>
        <path fill="#000000" d="M 51.53125 -28.171875 C 51.53125 -26.640625 50.867188 -25.875 49.546875 -25.875 L 14.453125 -25.875 L 14.453125 -24.40625 C 14.453125 -18.1875 15.671875 -13.785156 18.109375 -11.203125 C 20.554688 -8.617188 24.191406 -7.328125 29.015625 -7.328125 C 32.085938 -7.328125 35.367188 -7.429688 38.859375 -7.640625 C 42.347656 -7.859375 45.28125 -8.140625 47.65625 -8.484375 C 47.800781 -8.484375 47.90625 -8.484375 47.96875 -8.484375 C 48.039062 -8.484375 48.113281 -8.484375 48.1875 -8.484375 C 48.601562 -8.484375 48.984375 -8.378906 49.328125 -8.171875 C 49.679688 -7.960938 49.859375 -7.578125 49.859375 -7.015625 L 49.859375 -3.453125 C 49.859375 -2.753906 49.71875 -2.210938 49.4375 -1.828125 C 49.15625 -1.441406 48.597656 -1.179688 47.765625 -1.046875 C 44.765625 -0.484375 41.757812 -0.046875 38.75 0.265625 C 35.75 0.578125 32.503906 0.734375 29.015625 0.734375 C 26.015625 0.734375 23.097656 0.332031 20.265625 -0.46875 C 17.441406 -1.269531 14.894531 -2.613281 12.625 -4.5 C 10.351562 -6.382812 8.550781 -8.953125 7.21875 -12.203125 C 5.894531 -15.453125 5.234375 -19.519531 5.234375 -24.40625 L 5.234375 -33.828125 C 5.234375 -41.304688 7.238281 -47.257812 11.25 -51.6875 C 15.269531 -56.125 21.015625 -58.34375 28.484375 -58.34375 C 36.171875 -58.34375 41.929688 -56.125 45.765625 -51.6875 C 49.609375 -47.257812 51.53125 -41.304688 51.53125 -33.828125 Z M 42.21875 -32.890625 L 42.21875 -33.828125 C 42.21875 -39.765625 41.082031 -43.988281 38.8125 -46.5 C 36.539062 -49.019531 33.097656 -50.28125 28.484375 -50.28125 C 24.015625 -50.28125 20.554688 -48.953125 18.109375 -46.296875 C 15.671875 -43.640625 14.453125 -39.484375 14.453125 -33.828125 L 14.453125 -32.890625 Z M 42.21875 -32.890625" transform="translate(176.891421, 214.21332)"/>
    </svg>
);

// 2. Updated AppLogo Component (Now flexible!)
// It defaults to 'h-10' (40px) if no size is passed, ensuring it never defaults to huge.
const AppLogo = ({ className }) => (
    <div className={`flex items-center justify-center select-none ${className ? '' : 'mb-8'}`}>
        <RuneQRLogo className={className || "h-10 w-auto sm:h-12"} />
    </div>
);

const Button = ({
    children,
    onClick,
    variant = 'primary',
    className = '',
    type = 'button',
    isLoading = false,
    disabled = false
}) => {
    const baseClasses =
        "flex items-center justify-center font-semibold rounded-lg px-4 py-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";
    const variantClasses = {
        primary:
            'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm hover:shadow-md',
        secondary:
            'bg-gray-200 text-gray-800 hover:bg-gray-300 focus:ring-gray-400',
        danger:
            'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500 shadow-sm hover:shadow-md',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 focus:ring-gray-400'
    };
    return (
        <button
            type={type}
            onClick={onClick}
            className={`${baseClasses} ${variantClasses[variant]} ${className}`}
            disabled={isLoading || disabled}
        >
            {isLoading ? <Loader2 className="animate-spin" /> : children}
        </button>
    );
};

const Input = React.forwardRef(({ type = 'text', ...props }, ref) => (
    <input
        type={type}
        ref={ref}
        {...props}
        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
    />
));

const Modal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md m-4 animate-scale-in"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center p-5 border-b border-gray-100">
                    <h2 className="text-xl font-bold text-gray-800">{title}</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X />
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

const SkeletonLoader = () => (
    <div className="bg-white rounded-xl shadow border border-gray-200 p-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded-lg w-3/4 mb-4" />
        <div className="h-3 bg-gray-200 rounded-lg w-1/2 mb-2" />
        <div className="h-3 bg-gray-200 rounded-lg w-full" />
    </div>
);

// --- AUTH SCREEN ---
const AuthScreen = ({ authService }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isLogin, setIsLogin] = useState(true);
    const [verificationSent, setVerificationSent] = useState(false);

    const handleAuth = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setVerificationSent(false);
        try {
            if (isLogin) {
                await authService.login(email, password);
            } else {
                await authService.signup(email, password);
                setVerificationSent(true);
            }
        } catch (err) {
            const genericLoginError =
                'Invalid email or password. Please check your credentials and try again.';
            switch (err.code) {
                case 'auth/invalid-credential':
                case 'auth/wrong-password':
                case 'auth/user-not-found':
                    setError(genericLoginError);
                    break;
                case 'auth/email-already-in-use':
                    setError(
                        'This email is already registered. Please sign in or use a different email.'
                    );
                    break;
                default:
                    setError('An authentication error occurred. Please try again.');
                    console.error("Unhandled Auth Error:", err);
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleAuth = async () => {
        setIsLoading(true);
        setError('');
        try {
            await authService.googleLogin();
        } catch (err) {
            setError(err.message || 'Google sign-in failed.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full mx-auto">
                <div className="text-center mb-8">
                    <div className="flex justify-center items-center mb-8">
                        <AppLogo className="h-14 w-auto" />
                    </div>
                </div>
                {verificationSent ? (
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100 text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">
                            Verify Your Email
                        </h2>
                        <p className="text-gray-600 mt-4">
                            A verification link has been sent to{' '}
                            <strong className="font-semibold text-gray-900">
                                {email}
                            </strong>
                            .
                        </p>
                        <p className="text-gray-500 mt-2 text-sm">
                            Please check your inbox and click the link to activate your
                            account.
                        </p>
                        <Button
                            onClick={async () => {
                                await authService.logout();
                                setVerificationSent(false);
                                setIsLogin(true);
                            }}
                            variant="primary"
                            className="w-full mt-6"
                        >
                            Back to Sign In
                        </Button>
                    </div>
                ) : (
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                        <p className="text-gray-500 text-center mb-6">
                            {isLogin
                                ? 'Log in to manage your links.'
                                : 'Create an account to get started.'}
                        </p>
                        <form onSubmit={handleAuth} className="space-y-6">
                            {error && (
                                <p className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg">
                                    {error}
                                </p>
                            )}
                            <div>
                                <label className="text-sm font-medium text-gray-700 block mb-2">
                                    Email Address
                                </label>
                                <Input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-gray-700 block mb-2">
                                    Password
                                </label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                            </div>
                            <Button
                                type="submit"
                                variant="primary"
                                className="w-full"
                                isLoading={isLoading}
                                disabled={!email || !password}
                            >
                                {isLogin ? 'Sign In' : 'Create Account'}
                            </Button>
                        </form>
                        <div className="text-center mt-4">
                            <button
                                onClick={() => setIsLogin(!isLogin)}
                                className="text-sm text-blue-600 hover:underline"
                            >
                                {isLogin
                                    ? "Don't have an account? Sign Up"
                                    : 'Already have an account? Sign In'}
                            </button>
                        </div>
                        <div className="relative my-6">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-white px-2 text-gray-500">
                                    Or
                                </span>
                            </div>
                        </div>
                        <Button
                            onClick={handleGoogleAuth}
                            variant="secondary"
                            className="w-full"
                            isLoading={isLoading}
                        >
                            <svg
                                className="w-5 h-5 mr-2"
                                width="20"
                                height="20"
                                viewBox="0 0 48 48"
                            >
                                <path
                                    fill="#FFC107"
                                    d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                                />
                                <path
                                    fill="#FF3D00"
                                    d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                                />
                                <path
                                    fill="#4CAF50"
                                    d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.222,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                                />
                                <path
                                    fill="#1976D2"
                                    d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.574l6.19,5.238C42.02,35.636,44,30.138,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                                />
                            </svg>
                            Continue with Google
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- HEADER ---
const Header = ({ authService }) => {
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await authService.logout();
        } catch (e) {
            console.error(e);
            setIsLoggingOut(false);
        }
    };
    return (
        <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center h-16">
                    <AppLogo />
                    <Button
                        onClick={handleLogout}
                        variant="secondary"
                        isLoading={isLoggingOut}
                    >
                        <LogOut size={18} />
                        <span className="ml-2 hidden sm:inline">Log Out</span>
                    </Button>
                </div>
            </div>
        </header>
    );
};

// --- LINK EDITOR MODAL ---

// --- LINK EDITOR MODAL (UPDATED FOR FEEDBACK FORM GUIDANCE) ---

// --- LINK EDITOR MODAL (UPDATED FOR GOOGLE & MICROSOFT FORMS) ---

const LinkEditorModal = ({ isOpen, onClose, onSave, link }) => {
    const [formData, setFormData] = useState({
        name: '',
        destinationUrl: '',
        isDynamic: true,
        notes: '',
        type: 'generic'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [safeCheckError, setSafeCheckError] = useState('');
    const [isCheckingSafety, setIsCheckingSafety] = useState(false);
    const [isAISectionOpen, setIsAISectionOpen] = useState(false);
    const [aiPrompt, setAIPrompt] = useState("");
    const [isGeneratingAI, setIsGeneratingAI] = useState(false);
    // Step navigation: 0 = Basics, 1 = Destination, 2 = Notes
    const [activeStep, setActiveStep] = useState(0);
    const [shortUrlCopied, setShortUrlCopied] = useState(false);




    // ✅ Track if user has manually edited name/notes
    const [hasEditedName, setHasEditedName] = useState(false);
    const [hasEditedNotes, setHasEditedNotes] = useState(false);

    useEffect(() => {
        if (link) {
            setFormData({
                name: link.name,
                destinationUrl: link.destinationUrl,
                isDynamic: link.isDynamic,
                notes: link.notes || '',
                type: link.type || 'generic'
            });
            setHasEditedName(true);
            setHasEditedNotes(!!link.notes);
        } else {
            setFormData({
                name: '',
                destinationUrl: '',
                isDynamic: true,
                notes: '',
                type: 'generic'
            });
            setHasEditedName(false);
            setHasEditedNotes(false);
        }
        setSafeCheckError('');
        setIsCheckingSafety(false);
        setIsLoading(false);
    }, [link, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            // Modal just closed: clear AI prompt and section
            setAIPrompt("");
            setIsAISectionOpen(false);
            setIsGeneratingAI(false);
        }
    }, [isOpen]);


    // ✅ Smart defaults when switching to feedback type
    useEffect(() => {
        // Only auto-fill on new links (not when editing existing ones)
        if (link) return;

        if (formData.type === 'feedback') {
            // Pre-fill Name if user hasn't manually edited it
            if (!hasEditedName && !formData.name.trim()) {
                const today = new Date();
                const dateStr = today.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                });
                setFormData(p => ({
                    ...p,
                    name: `Feedback – ${dateStr}`
                }));
            }

            // Pre-fill Notes if user hasn't manually edited it
            if (!hasEditedNotes && !formData.notes.trim()) {
                setFormData(p => ({
                    ...p,
                    notes: 'Customer feedback, responses stored in Google Forms/Sheets or Microsoft Forms.'
                }));
            }
        }
    }, [formData.type, link, hasEditedName, hasEditedNotes, formData.name, formData.notes]);

    const handleCopyShortUrl = () => {
        if (!link?.shortId) return;
        const copyValue = `https://${APP_DOMAIN}/qr/${link.shortId}`;

        const textArea = document.createElement("textarea");
        textArea.value = copyValue;
        textArea.style.position = "fixed";
        textArea.style.top = "-9999px";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            document.execCommand("copy");
            setShortUrlCopied(true);
            setTimeout(() => setShortUrlCopied(false), 2000);
        } catch (err) {
            console.error("Failed to copy short URL", err);
        }

        document.body.removeChild(textArea);
    };


    const handleUrlBlur = async () => {
        setSafeCheckError('');
        const value = formData.destinationUrl.trim();
        if (!isValidUrl(value)) return;

        setIsCheckingSafety(true);

        try {
            const safeResult = await checkUrlSafetyClient(value);
            const safe = Array.isArray(safeResult) ? safeResult[0] : safeResult?.safe ?? safeResult;

            if (!safe) {
                console.warn('Safe Browsing flagged URL:', value);
                setSafeCheckError('This URL appears to be unsafe (malware, phishing, or unwanted software). Please use a different destination.');
                setIsCheckingSafety(false);
                return;
            }

            const adultResult = await checkAdultUrl(value);
            const isAdult = Array.isArray(adultResult) ? adultResult[0] : adultResult?.isAdult ?? adultResult;
            const reason = Array.isArray(adultResult) ? adultResult[1] : adultResult?.reason;

            if (isAdult) {
                console.warn('Adult/NSFW URL blocked:', value, reason);
                setSafeCheckError(reason || `This URL appears to contain adult or explicit content and cannot be used with ${APP_NAME}.`);
            }
        } catch (error) {
            console.error('URL validation error:', error);
        } finally {
            setIsCheckingSafety(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(p => ({ ...p, [name]: value }));

        // Track manual edits
        if (name === 'name') setHasEditedName(true);
        if (name === 'notes') setHasEditedNotes(true);

        if (name === 'destinationUrl') {
            setSafeCheckError('');
        }
    };

    const handleToggle = () => {
        setFormData(p => ({ ...p, isDynamic: !p.isDynamic }));
    };

    const isValidUrl = (s) => {
        try {
            new URL(s);
            return true;
        } catch {
            return false;
        }
    };

    const toggleAISection = () => {
        setIsAISectionOpen((prev) => !prev);
    };

    const handleAIPromptChange = (e) => {
        setAIPrompt(e.target.value);
    };

    const handleGenerateAI = async () => {
        const prompt = aiPrompt.trim();
        if (!prompt || prompt.length < 5) {
            alert("Please describe what this link is for (at least 5 characters).");
            return;
        }

        setIsGeneratingAI(true);
        try {
            const typeForAI = formData.type || "generic";
            const result = await generateAIContent(prompt, typeForAI);

            setFormData((prev) => {
                const nameFromAI = result.suggestedName?.trim() || "";
                const notesFromAI = result.suggestedNotes?.trim() || "";

                return {
                    ...prev,
                    name: prev.name?.trim()
                        ? `${prev.name.trim()} // ${nameFromAI}`
                        : nameFromAI,
                    notes: prev.notes?.trim()
                        ? `${prev.notes.trim()} // ${notesFromAI}`
                        : notesFromAI,
                };
            });

        } catch (err) {
            console.error("AI generate failed", err);
            alert(err.message || "AI couldn't help right now. Please try again.");
        } finally {
            setIsGeneratingAI(false);
        }
    };


    const isFormValid = !!formData.name.trim() && isValidUrl(formData.destinationUrl) && !safeCheckError;
    const isBusy = isLoading || isGeneratingAI;


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isFormValid || safeCheckError) return;

        setIsLoading(true);

        try {
            const value = formData.destinationUrl.trim();

            try {
                const safeResult = await checkUrlSafetyClient(value);
                const safe = Array.isArray(safeResult) ? safeResult[0] : safeResult?.safe ?? safeResult;

                if (!safe) {
                    console.warn('Blocked malicious URL on submit:', value);
                    setSafeCheckError('This URL is reported as unsafe by Google Safe Browsing and cannot be used.');
                    return;
                }

                const adultResult = await checkAdultUrl(value);
                const isAdult = Array.isArray(adultResult) ? adultResult[0] : adultResult?.isAdult ?? adultResult;
                const reason = Array.isArray(adultResult) ? adultResult[1] : adultResult?.reason;

                if (isAdult) {
                    console.warn('Blocked adult/NSFW URL on submit:', value, reason);
                    setSafeCheckError(reason || `This URL appears to contain adult or explicit content and cannot be used with ${APP_NAME}.`);
                    return;
                }
            } catch (validationError) {
                console.error('Validation error on submit:', validationError);
            }

            await onSave(formData);
            onClose();
        } catch (err) {
            console.error('LinkEditorModal Save operation failed:', err);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ UPDATED: Platform-agnostic labels and helper text
    const urlFieldConfig = formData.type === 'feedback'
        ? {
            label: 'Feedback Form URL',
            placeholder: 'https://docs.google.com/forms/... or https://forms.office.com/...',
            helperText: (
                <span>
                    Paste your form link here.<br />
                    <strong className="font-medium">Google Forms:</strong> Open your form → Send → copy link<br />
                    <strong className="font-medium">Microsoft Forms:</strong> Open your form → Share → Copy link
                </span>
            )
        }
        : {
            label: 'Destination URL',
            placeholder: 'https://example.com/your-long-url',
            helperText: null
        };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={link ? 'Edit Link' : 'Create New Link'}>
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* STEP SEGMENTED CONTROL */}
                <div className="flex mb-4">
                    <div className="inline-flex bg-gray-100 rounded-xl p-1 w-full">
                        {['Basics', 'Destination', 'Notes'].map((label, index) => (
                            <button
                                key={label}
                                type="button"
                                onClick={() => setActiveStep(index)}
                                className={[
                                    'flex-1 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200',
                                    activeStep === index
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-900'
                                ].join(' ')}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>
                {/* NAME FIELD + AI ASSIST */}
                {activeStep === 0 && (
                    <div className="space-y-4">
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700 block">
                                    Name
                                </label>

                                <button
                                    type="button"
                                    onClick={toggleAISection}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 px-2 py-1 rounded-full hover:bg-blue-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span className="hidden sm:inline">AI Assist</span>
                                </button>
                            </div>

                            {/* Input with subtle shimmer overlay when AI is writing */}
                            <div className={`relative ${isGeneratingAI ? "ai-shimmer" : ""}`}>
                                <Input
                                    name="name"
                                    placeholder="e.g., Customer Feedback – Main Branch"
                                    value={formData.name}
                                    onChange={handleChange}
                                    disabled={isGeneratingAI || isLoading}
                                />
                                {isGeneratingAI && (
                                    <div className="pointer-events-none absolute inset-0 rounded-lg border border-blue-100/70 bg-gradient-to-r from-transparent via-blue-50/80 to-transparent bg-[length:200%_100%] animate-ai-shimmer"></div>
                                )}
                            </div>

                            <p className="mt-1 text-xs text-gray-500">
                                Give this link a clear name so you can recognize it later.
                            </p>

                            {/* Collapsible AI prompt area */}
                            {isAISectionOpen && (
                                <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-3 space-y-2">
                                    <label className="text-xs font-medium text-gray-600 block mb-1">
                                        What is this link for?
                                    </label>
                                    <textarea
                                        rows={2}
                                        className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
                                        placeholder={
                                            formData.type === "feedback"
                                                ? "e.g., Customer feedback form for my bakery after in‑store visits."
                                                : "e.g., Menu QR for cafe tables, or promo page for December campaign."
                                        }
                                        value={aiPrompt}
                                        onChange={handleAIPromptChange}
                                        disabled={isGeneratingAI || isLoading}
                                    />
                                    <div className="flex items-center justify-between">
                                        <p className="text-[11px] text-gray-500">
                                            AI will suggest a title and notes. You can still edit them.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={handleGenerateAI}
                                            disabled={isGeneratingAI || isLoading || !aiPrompt.trim()}
                                            className="inline-flex items-center gap-1 rounded-lg bg-blue-600 text-white text-xs font-medium px-3 py-1.5 shadow-sm hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                        >
                                            {isGeneratingAI ? (
                                                <>
                                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    <span>Generating…</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles className="w-3.5 h-3.5" />
                                                    <span>Generate</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>


                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* LINK TYPE SELECTOR (Apple-style segmented control) */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        <div>
                            <label className="text-sm font-medium text-gray-700 block mb-2">Link Type</label>
                            <div className="inline-flex bg-gray-100 rounded-lg p-1 w-full">
                                <button
                                    type="button"
                                    onClick={() => setFormData(p => ({ ...p, type: 'generic' }))}
                                    className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${formData.type === 'generic'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    🔗 General Link
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData(p => ({ ...p, type: 'feedback' }))}
                                    className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 ${formData.type === 'feedback'
                                        ? 'bg-white text-gray-900 shadow-sm'
                                        : 'text-gray-600 hover:text-gray-900'
                                        }`}
                                >
                                    💬 Feedback Form
                                </button>
                            </div>
                            <p className="mt-1.5 text-xs text-gray-500">
                                {formData.type === 'generic'
                                    ? 'A standard link for websites, landing pages, or content.'
                                    : 'A link to a feedback form, survey, or review page (Google or Microsoft Forms).'}
                            </p>
                        </div>
                    </div>
                )}
                {activeStep === 1 && (
                    <div className="space-y-4">
                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* URL FIELD (contextual label & helper based on type) */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        <div>
                            <label className="text-sm font-medium text-gray-700 block mb-2">
                                {urlFieldConfig.label}
                            </label>
                            <Input
                                name="destinationUrl"
                                placeholder={urlFieldConfig.placeholder}
                                value={formData.destinationUrl}
                                onChange={handleChange}
                                onBlur={handleUrlBlur}
                            />

                            {/* ✅ UPDATED: Multi-line helper text for both platforms */}
                            {urlFieldConfig.helperText && (
                                <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">
                                    {urlFieldConfig.helperText}
                                </p>
                            )}

                            {isCheckingSafety && (
                                <p className="mt-1.5 text-xs text-blue-600 flex items-center gap-1">
                                    <Loader2 size={12} className="animate-spin" />
                                    Checking URL safety...
                                </p>
                            )}

                            {safeCheckError && (
                                <p className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                                    {safeCheckError}
                                </p>
                            )}
                        </div>

                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* DYNAMIC TOGGLE */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                            <div>
                                <label className="font-medium text-gray-800">Dynamic Link</label>
                                <p className="text-xs text-gray-500">Destination can be changed later.</p>
                            </div>
                            <button
                                type="button"
                                onClick={handleToggle}
                                className={`${formData.isDynamic ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                            >
                                <span className={`${formData.isDynamic ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition-transform`} />
                            </button>
                        </div>
                    </div>
                )}

                {activeStep === 2 && (
                    <div className="space-y-4">
                        {/* NOTES FIELD */}
                        <div>
                            <label className="text-sm font-medium text-gray-700 block mb-2">
                                Notes <span className="text-xs text-gray-400">(optional)</span>
                            </label>

                            <div className={`relative ${isGeneratingAI ? "ai-shimmer" : ""}`}>
                                <textarea
                                    name="notes"
                                    rows={3}
                                    placeholder={
                                        formData.type === "feedback"
                                            ? "e.g., Event feedback, post-purchase survey, customer satisfaction."
                                            : "e.g., Used on business card, Q4 promo, or event booth."
                                    }
                                    value={formData.notes}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                                    disabled={isGeneratingAI || isLoading}
                                />
                                {isGeneratingAI && (
                                    <div className="pointer-events-none absolute inset-0 rounded-lg border border-blue-100/70 bg-gradient-to-r from-transparent via-blue-50/80 to-transparent bg-[length:200%_100%] animate-ai-shimmer"></div>
                                )}
                            </div>
                        </div>


                        {/* ═══════════════════════════════════════════════════════════ */}
                        {/* SHORT URL DISPLAY (for existing dynamic links) */}
                        {/* ═══════════════════════════════════════════════════════════ */}
                        {formData.isDynamic && link?.shortId && (
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-sm font-medium text-gray-700">
                                        Short URL
                                    </label>
                                    <button
                                        type="button"
                                        onClick={handleCopyShortUrl}
                                        className="flex items-center text-xs font-medium text-gray-600 hover:text-blue-600 px-2.5 py-1 rounded-md border border-gray-200 bg-white hover:border-blue-300 transition-colors shrink-0"
                                    >
                                        {shortUrlCopied ? (
                                            <span className="text-green-600">Copied</span>
                                        ) : (
                                            <>
                                                <Copy size={13} className="mr-1.5" />
                                                Copy
                                            </>
                                        )}
                                    </button>
                                </div>
                                <p className="text-blue-600 font-mono break-all text-sm">
                                    https://{APP_DOMAIN}/qr/{link.shortId}
                                </p>
                            </div>
                        )}



                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════════ */}
                {/* ACTION BUTTONS */}
                {/* ═══════════════════════════════════════════════════════════ */}

                {/* Tiny Back / Next buttons for steps */}
                <div className="flex justify-between mt-4">
                    <button
                        type="button"
                        onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                        disabled={activeStep === 0}
                        className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveStep((s) => Math.min(2, s + 1))}
                        disabled={activeStep === 2}
                        className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                    <Button
                        onClick={onClose}
                        variant="secondary"
                        disabled={isBusy}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        isLoading={isLoading}
                        disabled={!isFormValid || isBusy}
                    >
                        Save
                    </Button>
                </div>


            </form>
        </Modal>
    );
};



// --- QR CODE MODAL ---
const QRCodeModal = ({ isOpen, onClose, link }) => {
    const qrRef = useRef(null);
    if (!link) return null;

    const qrValue =
        link.isDynamic && link.shortId
            ? `https://${APP_DOMAIN}/qr/${link.shortId}`
            : link.destinationUrl;

    const downloadQRCode = () => {
        if (qrRef.current) {
            qrRef.current.download(`${link.name.replace(/ /g, '_')}-qrcode`);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Your QR Code">
            <div className="flex flex-col items-center space-y-4">
                <div className="p-4 bg-white border border-gray-100 rounded-xl">
                    <QRCodeCanvas value={qrValue} size={256} ref={qrRef} />
                </div>
                <p className="text-center font-semibold text-lg">{link.name}</p>
                <p className="text-center text-sm text-gray-500 break-all">
                    {qrValue}
                </p>
                <Button
                    onClick={downloadQRCode}
                    variant="primary"
                    className="w-full"
                >
                    Download PNG
                </Button>
            </div>
        </Modal>
    );
};

// --- LINK CARD ---
// --- LINK CARD (REDESIGNED - APPLE-STYLE) ---
const LinkCard = ({ link, onEdit, onDelete, onShowQR }) => {
    const [showCopySuccess, setShowCopySuccess] = useState(false);

    const handleCopy = () => {
        const copyValue = `https://${APP_DOMAIN}/qr/${link.shortId}`;
        const textArea = document.createElement('textarea');
        textArea.value = copyValue;
        textArea.style.position = 'fixed';
        textArea.style.top = '-9999px';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
            document.execCommand('copy');
            setShowCopySuccess(true);
            setTimeout(() => setShowCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
        document.body.removeChild(textArea);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex flex-col space-y-4 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5">

            {/* HEADER: Title + Badges (Dynamic & Feedback) */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-lg mb-1 truncate">
                        {link.name}
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                        {link.destinationUrl}
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    {/* ✅ NEW: Feedback Badge */}
                    {link.type === 'feedback' && (
                        <span className="flex items-center text-xs font-semibold text-purple-600 bg-purple-50 rounded-full px-2.5 py-1 whitespace-nowrap">
                            💬 Feedback
                        </span>
                    )}
                    {/* Existing Dynamic Badge */}
                    {link.isDynamic && (
                        <span className="flex items-center text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2.5 py-1 whitespace-nowrap">
                            <Zap size={12} className="mr-1" />
                            Dynamic
                        </span>
                    )}
                </div>
            </div>

            {/* SHORT URL + COPY (Only for dynamic links) */}
            {link.isDynamic && (
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2.5">
                    <p className="text-xs text-gray-600 font-mono truncate flex-1">
                        {`${APP_DOMAIN}/qr/${link.shortId}`}
                    </p>
                    <button
                        onClick={handleCopy}
                        className="flex items-center text-xs font-medium text-gray-600 hover:text-blue-600 px-2.5 py-1 rounded-md border border-gray-200 bg-white hover:border-blue-300 transition-colors shrink-0"
                    >
                        {showCopySuccess ? (
                            <>
                                <span className="text-green-600">✓ Copied</span>
                            </>
                        ) : (
                            <>
                                <Copy size={13} className="mr-1.5" />
                                Copy
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* STATS ROW */}
            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <div className="flex items-center gap-4">
                    {link.isDynamic ? (
                        <span className="text-sm text-gray-700">
                            <span className="font-semibold text-gray-900">
                                {(link.scanCount || 0).toLocaleString()}
                            </span>{' '}
                            <span className="text-gray-500">scans</span>
                        </span>
                    ) : (
                        <span className="text-sm text-gray-500">Static link</span>
                    )}

                    {link.isDynamic && link.scans && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="flex items-center">
                                📱 {link.scans?.ios || 0}
                            </span>
                            <span className="flex items-center">
                                🤖 {link.scans?.android || 0}
                            </span>
                            <span className="flex items-center">
                                💻 {link.scans?.other || 0}
                            </span>
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onEdit(link)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit link"
                    >
                        <FilePenLine size={16} />
                    </button>
                    <button
                        onClick={() => onShowQR(link)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="View QR code"
                    >
                        <QrCode size={16} />
                    </button>
                    <button
                        onClick={() => onDelete(link)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete link"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>

            {/* NOTES ROW */}
            {link.notes && (
                <div className="flex items-start gap-2 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                    <span className="text-amber-600 text-sm shrink-0">💡</span>
                    <p className="text-xs text-amber-800 leading-relaxed flex-1">
                        {link.notes}
                    </p>
                </div>
            )}

            {/* METADATA (Timestamps) */}
            <div className="text-xs space-y-0.5">
                <div className="grid grid-cols-[80px_1fr] gap-1.5">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-400">{formatDateTime(link.createdAt)}</span>
                </div>

                {link.updatedAt &&
                    link.updatedAt.getTime() !== link.createdAt?.getTime?.() && (
                        <div className="grid grid-cols-[80px_1fr] gap-1.5">
                            <span className="text-gray-500">Updated</span>
                            <span className="text-gray-400">{formatDateTime(link.updatedAt)}</span>
                        </div>
                    )}

                {link.isDynamic && link.lastScannedAt && (
                    <div className="grid grid-cols-[80px_1fr] gap-1.5">
                        <span className="text-gray-500">Last scanned</span>
                        <span className="text-gray-400">{formatDateTime(link.lastScannedAt)}</span>
                    </div>
                )}
            </div>

        </div>
    );
};



// --- DELETE CONFIRM MODAL ---
const DeleteConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    link,
    isLoading
}) => {
    if (!isOpen || !link) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Confirm Deletion">
            <div className="space-y-4">
                <p>
                    Are you sure you want to permanently delete the link named{' '}
                    <strong className="font-semibold">{link.name}</strong>?
                </p>
                <p className="text-sm text-gray-500">
                    This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3 pt-4">
                    <Button
                        onClick={onClose}
                        variant="secondary"
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={onConfirm}
                        variant="danger"
                        isLoading={isLoading}
                    >
                        Delete
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

// --- TOAST NOTIFICATION (APPLE-STYLE) ---
const Toast = ({ message, type = 'error', onClose }) => {
    useEffect(() => {
        const duration = type === 'error' ? 8000 : 5000; // 8s for errors, 5s for success
        const timer = setTimeout(onClose, duration);
        return () => clearTimeout(timer);
    }, [onClose, type]);

    const bgColors = {
        error: 'bg-red-50 border-red-200',
        success: 'bg-green-50 border-green-200',
        warning: 'bg-amber-50 border-amber-200'
    };

    const textColors = {
        error: 'text-red-800',
        success: 'text-green-800',
        warning: 'text-amber-800'
    };

    const icons = {
        error: '⚠️',
        success: '✓',
        warning: '⚡'
    };

    return (
        <div className="fixed top-20 right-4 z-50 animate-scale-in max-w-sm">
            <div className={`${bgColors[type]} border rounded-xl shadow-lg p-4 flex items-start gap-3`}>
                <span className="text-xl shrink-0">{icons[type]}</span>
                <div className="flex-1">
                    <p className={`${textColors[type]} text-sm font-medium leading-relaxed`}>
                        {message}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className={`${textColors[type]} hover:opacity-70 transition-opacity shrink-0`}
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};


// --- DASHBOARD (WITH ANALYTICS OVERVIEW) ---
const Dashboard = ({ user, authService }) => {
    const isOnline = useOnlineStatus();
    const { links, loading, createLink, updateLink, deleteLink } =
        useFirebaseFirestore(user);

    const [filterType, setFilterType] = useState('all');
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [isQROpen, setIsQROpen] = useState(false);
    const [selectedLink, setSelectedLink] = useState(null);
    const [sortBy, setSortBy] = useState('createdAt');

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'error') => {
        setToast({ message, type });
    };

    const closeToast = () => {
        setToast(null);
    };


    const stats = useMemo(() => {
        const totalLinks = links.length;
        const totalScans = links.reduce(
            (sum, link) => sum + (link.scanCount || 0),
            0
        );

        const deviceBreakdown = links.reduce(
            (acc, link) => {
                if (link.scans) {
                    acc.ios += link.scans.ios || 0;
                    acc.android += link.scans.android || 0;
                    acc.other += link.scans.other || 0;
                }
                return acc;
            },
            { ios: 0, android: 0, other: 0 }
        );

        const mobilePercentage =
            totalScans > 0
                ? Math.round(
                    ((deviceBreakdown.ios + deviceBreakdown.android) / totalScans) *
                    100
                )
                : 0;

        const topPerformer =
            links.length > 0
                ? links.reduce((top, link) =>
                    (link.scanCount || 0) > (top.scanCount || 0) ? link : top, links[0]
                )
                : null;

        return {
            totalLinks,
            totalScans,
            mobilePercentage,
            topPerformer,
            deviceBreakdown
        };
    }, [links]);

    const displayedLinks = useMemo(() => {
        let filtered = links;

        // Apply type filter if feature enabled
        if (FEATURES.SHOW_TYPE_FILTER && filterType !== 'all') {
            filtered = filtered.filter(link => (link.type || 'qr') === filterType);
        }

        // Apply sort
        const copy = [...filtered];
        switch (sortBy) {
            case 'createdAtAsc':
                return copy.sort((a, b) => (a.createdAt?.getTime?.() || 0) - (b.createdAt?.getTime?.() || 0));
            case 'updatedAtDesc':
                return copy.sort((a, b) => (b.updatedAt?.getTime?.() || 0) - (a.updatedAt?.getTime?.() || 0));
            case 'scansDesc':
                return copy.sort((a, b) => (b.scanCount || 0) - (a.scanCount || 0));
            case 'scansAsc':
                return copy.sort((a, b) => (a.scanCount || 0) - (b.scanCount || 0));
            case 'nameAsc':
                return copy.sort((a, b) => a.name.localeCompare(b.name));
            case 'createdAt':
            default:
                return copy.sort((a, b) => (b.createdAt?.getTime?.() || 0) - (a.createdAt?.getTime?.() || 0));
        }
    }, [links, filterType, sortBy]);

    // Update your create button to disable when offline
    const openCreateModal = () => {
        if (!isOnline) {
            showToast('You must be online to create QR codes.', 'error');
            return;
        }
        setSelectedLink(null);
        setIsEditorOpen(true);
    };

    const openEditModal = (link) => {
        if (!isOnline) {
            showToast('You must be online to edit links.', 'error');
            return;
        }
        setSelectedLink(link);
        setIsEditorOpen(true);
    };

    const openQRModal = (link) => {
        setSelectedLink(link);
        setIsQROpen(true);
    };

    // ════════════════════════════════════════════════════════════════════
    // HANDLE SAVE (CREATE/UPDATE) - Production Error Handling
    // ════════════════════════════════════════════════════════════════════
    // DASHBOARD – SAVE HANDLER WITH ROBUST ERROR HANDLING
    const handleSave = async (data) => {
        try {
            if (selectedLink) {
                await updateLink(selectedLink.id, data);
                showToast('Link updated successfully!', 'success');
            } else {
                await createLink(data);
                showToast('Link created successfully!', 'success');
            }
        } catch (error) {
            // Structured logging for debugging
            console.error('Save operation failed', {
                operation: selectedLink ? 'update' : 'create',
                linkId: selectedLink?.id,
                userId: user?.uid,
                errorMessage: error?.message,
                code: error?.code,
                stack: error?.stack,
            });

            let errorMessage = 'Unable to save link. Please try again.';

            // Network / timeout
            if (error?.message?.includes('timed out')) {
                errorMessage =
                    'Request timed out. Please check your internet connection and try again.';
            } else if (error?.code === 'unavailable') {
                errorMessage =
                    'Network error. Please check your connection and try again.';
            }
            // Permission / rules
            else if (
                error?.code === 'permission-denied' ||
                error?.message?.includes('PERMISSION_DENIED')
            ) {
                errorMessage =
                    'Permission denied. This action is not allowed. Please sign out and sign in again, or contact support.';
                console.error('Security rules violation', {
                    userId: user?.uid,
                    linkId: selectedLink?.id,
                    operation: selectedLink ? 'update' : 'create',
                });
            }
            // Validation
            else if (
                error?.message?.toLowerCase?.().includes('invalid') ||
                error?.code === 'invalid-argument'
            ) {
                errorMessage =
                    'Some of the values look invalid. Please double‑check the URL and fields, then try again.';
            }
            // Quota / resource
            else if (error?.code === 'resource-exhausted') {
                errorMessage = 'Service limit reached. Please try again a bit later.';
            } else if (error?.code === 'deadline-exceeded') {
                errorMessage =
                    'The server took too long to respond. Please try again in a moment.';
            }
            // Not found
            else if (error?.code === 'not-found') {
                errorMessage =
                    'This link could not be found. It may have been deleted in another session.';
            }

            showToast(errorMessage, 'error');

            // Re‑throw so LinkEditorModal keeps the modal open and shows inline error UX
            throw error;
        }
    };

    const openDeleteConfirm = (link) => {
        if (!isOnline) {
            showToast('You must be online to delete links.', 'error');
            return;
        }
        setLinkToDelete(link);
        setIsDeleteConfirmOpen(true);
    };

    const closeDeleteConfirm = () => {
        setLinkToDelete(null);
        setIsDeleteConfirmOpen(false);
    };



    // ════════════════════════════════════════════════════════════════════
    // HANDLE DELETE - Production Error Handling
    // ════════════════════════════════════════════════════════════════════
    // DASHBOARD – DELETE HANDLER WITH ROBUST ERROR HANDLING
    const handleConfirmDelete = async () => {
        if (!linkToDelete) return;

        setIsDeleting(true);
        try {
            await deleteLink(linkToDelete.id);
            showToast('Link deleted successfully.', 'success');
            setIsDeleting(false);
            closeDeleteConfirm();
        } catch (error) {
            console.error('Delete operation failed', {
                operation: 'delete',
                linkId: linkToDelete?.id,
                userId: user?.uid,
                errorMessage: error?.message,
                code: error?.code,
                stack: error?.stack,
            });

            let errorMessage = 'Unable to delete link. Please try again.';

            if (error?.message?.includes('timed out')) {
                errorMessage =
                    'Delete request timed out. Please check your internet connection and try again.';
            } else if (error?.code === 'unavailable') {
                errorMessage =
                    'Network error while deleting. Please check your connection and try again.';
            } else if (
                error?.code === 'permission-denied' ||
                error?.message?.includes('PERMISSION_DENIED')
            ) {
                errorMessage =
                    'Permission denied. You may no longer be the owner of this link.';
                console.error('Security rules violation on delete', {
                    userId: user?.uid,
                    linkId: linkToDelete?.id,
                });
            } else if (error?.code === 'not-found') {
                errorMessage =
                    'This link no longer exists. It may have already been deleted.';
            }

            showToast(errorMessage, 'error');
            setIsDeleting(false);
            // Keep the confirm modal open so the user can retry or cancel
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen">
            <Header authService={authService} />

            {/* ✅ NEW: Toast notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={closeToast}
                />
            )}

            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {!isOnline && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <p className="text-amber-800 text-sm font-medium">
                            ⚠️ You're offline. Create, update, and delete operations are disabled.
                        </p>
                    </div>
                )}
                {/* Analytics Overview Section */}
                {!loading && links.length > 0 && (
                    <div className="mb-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-6 sm:p-8 border border-blue-100 shadow-sm">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900">
                                📊 Analytics Overview
                            </h2>
                            <span className="text-sm text-gray-500">Real-time</span>
                        </div>

                        {/* Stats Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {/* Active Links */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-3xl font-bold text-gray-900 mb-1">
                                    {stats.totalLinks}
                                </div>
                                <div className="text-sm text-gray-600 font-medium">
                                    Active Links
                                </div>
                                <div className="mt-2 text-xs text-blue-600">
                                    🔗 Dynamic QR Codes
                                </div>
                            </div>

                            {/* Total Scans */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-3xl font-bold text-gray-900 mb-1">
                                    {stats.totalScans.toLocaleString()}
                                </div>
                                <div className="text-sm text-gray-600 font-medium">
                                    Total Scans
                                </div>
                                <div className="mt-2 text-xs text-green-600">
                                    📈 All time
                                </div>
                            </div>

                            {/* Mobile Users */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="text-3xl font-bold text-gray-900 mb-1">
                                    {stats.mobilePercentage}%
                                </div>
                                <div className="text-sm text-gray-600 font-medium">
                                    Mobile Users
                                </div>
                                <div className="mt-2 text-xs text-purple-600">
                                    <div className="flex flex-wrap gap-x-2 gap-y-1">
                                        <span className="truncate">
                                            📱 iOS: {stats.deviceBreakdown.ios}
                                        </span>
                                        <span className="truncate">
                                            🤖 <span className="hidden sm:inline">Android</span>
                                            <span className="sm:hidden">And</span>: {stats.deviceBreakdown.android}
                                        </span>
                                    </div>
                                </div>
                            </div>


                            {/* Top Performer */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex items-center mb-1">
                                    <span className="text-2xl mr-2">🏆</span>
                                    <div className="text-xl font-bold text-gray-900">
                                        {stats.topPerformer?.scanCount || 0}
                                    </div>
                                </div>
                                <div className="text-sm text-gray-600 font-medium">
                                    Top Performer
                                </div>
                                <div className="mt-2 text-xs text-orange-600 truncate">
                                    {stats.topPerformer?.name || 'N/A'}
                                </div>
                            </div>
                        </div>

                        {/* Device Distribution Chart */}
                        {stats.totalScans > 0 && (
                            <div className="bg-white rounded-2xl p-4 shadow-sm">
                                <div className="text-sm font-medium text-gray-700 mb-3">
                                    Device Distribution
                                </div>
                                <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                                    <div
                                        className="bg-blue-500 transition-all duration-500"
                                        style={{
                                            width: `${(stats.deviceBreakdown.ios / stats.totalScans) * 100}%`
                                        }}
                                        title={`iOS: ${stats.deviceBreakdown.ios}`}
                                    />
                                    <div
                                        className="bg-green-500 transition-all duration-500"
                                        style={{
                                            width: `${(stats.deviceBreakdown.android / stats.totalScans) * 100}%`
                                        }}
                                        title={`Android: ${stats.deviceBreakdown.android}`}
                                    />
                                    <div
                                        className="bg-gray-400 transition-all duration-500"
                                        style={{
                                            width: `${(stats.deviceBreakdown.other / stats.totalScans) * 100}%`
                                        }}
                                        title={`Other: ${stats.deviceBreakdown.other}`}
                                    />
                                </div>
                                <div className="flex justify-between mt-2 text-xs text-gray-600">
                                    <span>📱 iOS: {stats.deviceBreakdown.ios}</span>
                                    <span>🤖 Android: {stats.deviceBreakdown.android}</span>
                                    <span>💻 Other: {stats.deviceBreakdown.other}</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* ✅ CONDITIONAL: Type Filter Bar (hidden by default) */}
                {FEATURES.SHOW_TYPE_FILTER && (
                    <div className="mb-4 flex items-center gap-2">
                        <button
                            onClick={() => setFilterType('all')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterType === 'all'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            All Links
                        </button>
                        <button
                            onClick={() => setFilterType('qr')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterType === 'qr'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            📱 QR Codes
                        </button>
                        <button
                            onClick={() => setFilterType('campaign')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterType === 'campaign'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            📢 Campaigns
                        </button>
                        <button
                            onClick={() => setFilterType('redirect')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${filterType === 'redirect'
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                }`}
                        >
                            🔗 Redirects
                        </button>
                    </div>
                )}


                {/* Link Library Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
                    <h1 className="text-3xl font-bold text-gray-900">Link Library</h1>
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="createdAt">Newest first</option>
                            <option value="createdAtAsc">Oldest first</option>
                            <option value="updatedAtDesc">Recently updated</option>
                            <option value="scansDesc">Most scans</option>
                            <option value="scansAsc">Fewest scans</option>
                            <option value="nameAsc">Name (A-Z)</option>
                        </select>
                        <Button onClick={openCreateModal} variant="primary">
                            <Plus size={20} className="mr-2 -ml-1" />
                            Create New Link
                        </Button>
                    </div>
                </div>

                {/* Links Grid */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, i) => (
                            <SkeletonLoader key={i} />
                        ))}
                    </div>
                ) : links.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-lg border-2 border-dashed">
                        <h2 className="text-xl font-semibold text-gray-700">
                            No links yet!
                        </h2>
                        <p className="text-gray-500 mt-2">
                            Click "Create New Link" to get started.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {displayedLinks.map((link) => (
                            <LinkCard
                                key={link.id}
                                link={link}
                                onEdit={openEditModal}
                                onDelete={openDeleteConfirm}
                                onShowQR={openQRModal}
                            />
                        ))}
                    </div>
                )}
            </main>

            {/* Modals */}
            <LinkEditorModal
                isOpen={isEditorOpen}
                onClose={() => setIsEditorOpen(false)}
                onSave={handleSave}
                link={selectedLink}
            />
            <QRCodeModal
                isOpen={isQROpen}
                onClose={() => setIsQROpen(false)}
                link={selectedLink}
            />
            <DeleteConfirmModal
                isOpen={isDeleteConfirmOpen}
                onClose={closeDeleteConfirm}
                onConfirm={handleConfirmDelete}
                link={linkToDelete}
                isLoading={isDeleting}
            />
        </div>
    );
};

// --- VERIFY EMAIL SCREEN ---
const VerifyEmailScreen = ({ user, authService }) => {
    const [isResending, setIsResending] = useState(false);
    const [resendMessage, setResendMessage] = useState('');
    const [verificationSuccess, setVerificationSuccess] = useState(false);

    useEffect(() => {
        const interval = setInterval(async () => {
            if (auth.currentUser) {
                await auth.currentUser.reload();
                if (auth.currentUser.emailVerified) {
                    clearInterval(interval);
                    setVerificationSuccess(true);
                    setTimeout(() => authService.logout(), 3000);
                }
            }
        }, 3000);
        return () => clearInterval(interval);
    }, [authService]);

    const handleResend = async () => {
        setIsResending(true);
        setResendMessage('');
        try {
            await authService.resendVerificationEmail();
            setResendMessage('A new verification email has been sent!');
        } catch (error) {
            setResendMessage('Failed to send email. Please try again in a moment.');
        } finally {
            setIsResending(false);
            setTimeout(() => setResendMessage(''), 5000);
        }
    };

    if (verificationSuccess) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
                <div className="max-w-md w-full mx-auto text-center bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                    <div className="flex justify-center items-center mb-4">
                        <AppLogo />
                    </div>
                    <h2 className="text-2xl font-bold text-green-600 mb-2">
                        Success!
                    </h2>
                    <p className="text-gray-600 mt-4">
                        Your email has been verified.
                    </p>
                    <p className="text-gray-500 mt-2 text-sm">
                        Redirecting you to the sign-in page...
                    </p>
                    <div className="mt-4">
                        <Loader2 className="animate-spin inline-block" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full mx-auto text-center bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                <div className="flex justify-center items-center mb-4">
                    <AppLogo />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    Almost There!
                </h2>
                <p className="text-gray-600 mt-4">
                    Please verify your email address. We've sent a confirmation link to
                </p>
                <p className="font-semibold text-gray-900 my-2">{user.email}</p>
                <p className="text-gray-500 mt-2 text-sm">
                    Check your inbox and spam folder to activate your account.
                </p>
                <Button
                    onClick={handleResend}
                    variant="secondary"
                    className="w-full mt-6"
                    isLoading={isResending}
                >
                    Resend Verification Email
                </Button>
                {resendMessage && (
                    <p className="text-sm text-green-600 mt-2">{resendMessage}</p>
                )}
                <Button
                    onClick={authService.logout}
                    variant="primary"
                    className="w-full mt-4"
                >
                    Back to Sign In
                </Button>
            </div>
        </div>
    );
};

// --- REDIRECTOR (PRODUCTION ERROR HANDLING) ---
// REDIRECTOR – MAIN COMPONENT (snippet)

const Redirector = () => {
    const [status, setStatus] = useState('loading'); // 'loading' | 'redirecting' | 'error'
    const [errorDetails, setErrorDetails] = useState({
        title: '',
        message: '',
        technical: '',
    });

            // [KEEP] Ref to prevent double-firing in React Strict Mode (fixes localhost double-count)
        const hasTrackedScan = useRef(false);

        const handleRedirect = async () => {
            const path = window.location.pathname;
            const shortId = path.split('/qr/')[1];

            // CASE 1: Missing or invalid shortId in path
            if (!shortId || !shortId.trim()) {
                console.error('Redirect failed: Missing shortId in URL path', {
                    path,
                    shortId,
                });
                setStatus('error');
                setErrorDetails({
                    title: 'Invalid Link',
                    message:
                        'The QR code link format is incorrect. Please check the URL or scan the code again.',
                    technical: 'ERR_MISSING_SHORT_ID',
                });
                return;
            }

            if (shortId.length < 6 || shortId.length > 12) {
                console.error('Redirect failed: Invalid shortId length', { shortId });
                setStatus('error');
                setErrorDetails({
                    title: 'Invalid Link',
                    message:
                        `The link code is not valid. Please scan a valid ${APP_NAME} code.`,
                    technical: 'ERR_INVALID_SHORT_ID_FORMAT',
                });
                return;
            }

            try {
                // CASE 2: public_links/{shortId} not found
                const publicLinkRef = doc(db, 'public_links', shortId);
                const docSnap = await getDoc(publicLinkRef);

                if (!docSnap.exists()) {
                    console.error('Redirect failed: Link not found in database', {
                        shortId,
                    });
                    setStatus('error');
                    setErrorDetails({
                        title: 'Link Not Found',
                        message:
                            'This QR code no longer exists or has been removed by its owner.',
                        technical: 'ERR_LINK_NOT_FOUND',
                    });
                    return;
                }

                const linkData = docSnap.data();
                const { destinationUrl, ownerUid, privateLinkDocId } = linkData || {};

                // Validate destinationUrl
                if (
                    !destinationUrl ||
                    typeof destinationUrl !== 'string' ||
                    !destinationUrl.startsWith('http')
                ) {
                    console.error(
                        'Redirect failed: Invalid destination URL in document',
                        { shortId, linkData }
                    );
                    setStatus('error');
                    setErrorDetails({
                        title: 'Configuration Error',
                        message:
                            'This link is misconfigured. Please contact the link owner.',
                        technical: 'ERR_INVALID_DESTINATION',
                    });
                    return;
                }

                // CASE 3: Server-side scan tracking via Cloud Function
                // [CHANGED] Added check to ensure tracking only runs once per session/mount
                if (ownerUid && privateLinkDocId && !hasTrackedScan.current) {
                    hasTrackedScan.current = true; // Mark as tracked immediately

                    // Do NOT await; redirect must be instant.
                    fetch(TRACK_SCAN_HTTP_URL, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ shortId }),
                        keepalive: true, // Critical for tracking during redirect
                    }).catch((err) => {
                        console.error("trackScanHttp failed (non-critical)", {
                            message: err?.message,
                            shortId,
                        });
                    });
                }

                // Success redirect behavior unchanged
                setStatus('redirecting');
                window.location.assign(destinationUrl);
            } catch (error) {
                console.error('Redirect failed with unexpected error', {
                    errorMessage: error?.message,
                    code: error?.code,
                    stack: error?.stack,
                });

                // Granular messaging for network / rules
                if (error?.code === 'unavailable') {
                    setErrorDetails({
                        title: 'Connection Error',
                        message:
                            'Unable to reach the server. Please check your internet connection and try again.',
                        technical: 'ERR_FIRESTORE_UNAVAILABLE',
                    });
                } else if (error?.code === 'permission-denied') {
                    setErrorDetails({
                        title: 'Access Denied',
                        message:
                            'You do not have permission to access this link. It may be private or restricted.',
                        technical: 'ERR_PERMISSION_DENIED',
                    });
                } else {
                    setErrorDetails({
                        title: 'Unexpected Error',
                        message:
                            'Something went wrong while processing this link. Please try again later.',
                        technical: `ERR_UNKNOWN: ${error?.message || 'No message'}`,
                    });
                }

                setStatus('error');
            }
        };

        handleRedirect();
    

    // LOADING STATE – use your premium loader
    if (status === 'loading') {
        return (
            <AnimatedQRLoader
                variant="app"
                fullScreen
                message="Resolving link..."
                size="default"
            />
        );
    }

    // REDIRECTING STATE – unchanged success behavior
    if (status === 'redirecting') {
        return (
            <AnimatedQRLoader
                variant="redirect"
                fullScreen
                size="default"
            />
        );
    }

    // ERROR STATE – polished card, already aligned with DynQR/RuneQR styling
    if (status === 'error') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 max-w-md w-full">
                    {/* Error icon */}
                    <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
                        <X className="text-red-500" size={40} strokeWidth={2.5} />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                        {errorDetails.title || 'Something went wrong'}
                    </h2>

                    {/* Message */}
                    <p className="text-gray-600 text-sm leading-relaxed mb-6 text-center">
                        {errorDetails.message ||
                            'We could not open this link. Please try again or contact the link owner.'}
                    </p>

                    {/* Actions */}
                    <div className="flex flex-col gap-3">
                        <a
                            href="/"
                            className="w-full bg-blue-600 text-white text-center font-semibold text-sm px-6 py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            Go to {APP_NAME} Homepage
                        </a>
                        <button
                            onClick={() => window.location.reload()}
                            className="w-full bg-gray-100 text-gray-700 text-center font-medium text-sm px-6 py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                        >
                            Try Again
                        </button>
                    </div>

                    {/* Technical details for debugging (collapsible) */}
                    <details className="mt-6">
                        <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 transition-colors text-center">
                            Technical details
                        </summary>
                        <p className="mt-2 text-xs text-gray-500 font-mono bg-gray-50 p-3 rounded-lg border border-gray-200 break-all">
                            {errorDetails.technical || 'N/A'}
                        </p>
                    </details>

                    {/* Footer hint */}
                    <p className="mt-6 text-xs text-gray-400 text-center">
                        If this problem persists, contact the link owner.
                    </p>
                </div>
            </div>
        );
    }

    return null;
};

// ============================================================
// ✅ MAIN APPLICATION (UPDATED WITH ANIMATED LOADER)
// ============================================================
function MainApplication() {
    const { user, loading, ...authService } = useFirebaseAuth();
    const [isTailwindLoaded, setIsTailwindLoaded] = useState(
        typeof window !== 'undefined' && !!window.tailwind
    );

    // Load QR Code Styling Library
    useEffect(() => {
        const scriptId = 'qrcode-styling';
        if (document.getElementById(scriptId)) return;

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = 'https://unpkg.com/qr-code-styling@1.5.0/lib/qr-code-styling.js';
        document.head.appendChild(script);
    }, []);

    // Load Tailwind CSS
    useEffect(() => {
        if (window.tailwind) {
            setIsTailwindLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.tailwindcss.com';
        script.onload = () => setIsTailwindLoaded(true);
        document.head.appendChild(script);
    }, []);

    // ✅ UPDATED: Professional loading with AnimatedQRLoader
    if (loading || !isTailwindLoaded) {
        return (
            <AnimatedQRLoader
                variant="app"
                fullScreen={true}
                size="large"
            />
        );
    }

    const renderContent = () => {
        if (!user) {
            return <AuthScreen authService={authService} />;
        }

        const isEmailProvider = user.providerData.some(
            (p) => p.providerId === 'password'
        );
        if (isEmailProvider && !user.emailVerified) {
            return <VerifyEmailScreen user={user} authService={authService} />;
        }

        return <Dashboard user={user} authService={authService} />;
    };

    return (
        <>
            <style>{keyframes}</style>
            {renderContent()}
        </>
    );
}

// --- APP ENTRY ---
export default function App() {
    const path = window.location.pathname;

    if (path.startsWith('/qr/')) {
        return <Redirector />;
    }

    return <MainApplication />;
}

// --- CSS ANIMATIONS ---
// --- CSS ANIMATIONS ---
const keyframes = `
/* Fade in animation */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fade-in {
  animation: fadeIn 0.2s ease-out forwards;
}

/* Delayed fade in */
@keyframes fadeInDelayed {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fade-in-delayed {
  animation: fadeInDelayed 0.4s ease-out 0.2s forwards;
  opacity: 0;
}

/* Scale in animation */
@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-scale-in {
  animation: scaleIn 0.2s ease-out forwards;
}
`;
